import { Create, Edit } from "@refinedev/antd";
import { useNotification } from "@refinedev/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Form, InputNumber, Select, Switch, Typography } from "antd";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";

import { api } from "../api";
import type { OfficePlan, PlanSeason } from "../types";
import { durationLabel } from "./plans";

type Values = {
  weeklyHours: number;
  /** "m:3" for 3 months, "s:<season id>" for a season plan. */
  duration: string;
  priceLira: number;
  monthlyQuestions: number;
  isFeatured: boolean;
  isActive: boolean;
};

const MONTHS = [1, 3, 6, 12].map((months) => ({
  value: `m:${months}`,
  label: durationLabel(months),
}));

const durationOf = (plan: OfficePlan) =>
  plan.season ? `s:${plan.season.id}` : `m:${plan.durationMonths ?? 1}`;

/**
 * /plans/new and /plans/:id (permission `plans.manage`). Weekly hours and duration are set at
 * creation and fixed after; the price applies to new purchases, current subscriptions keep their
 * period. Every change is in the audit log. A season plan (product decision 2026-10-11) runs
 * until its season ends and is priced per week.
 */
export function PlanEditPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Values>();
  const { data: plan, error } = useQuery({
    queryKey: ["office", "plan", id],
    queryFn: () => api<OfficePlan>(`/api/office/plans/${encodeURIComponent(id ?? "")}`),
    enabled: !isNew,
  });
  const { data: seasons } = useQuery({
    queryKey: ["office", "seasons"],
    queryFn: () => api<PlanSeason[]>("/api/office/seasons"),
    enabled: isNew,
  });
  const duration = Form.useWatch("duration", form);
  const isSeason = (duration ?? "").startsWith("s:");
  const durations = [
    ...MONTHS,
    ...(seasons ?? []).map((season) => ({ value: `s:${season.id}`, label: season.name })),
    ...(plan?.season ? [{ value: `s:${plan.season.id}`, label: plan.season.name }] : []),
  ];

  useEffect(() => {
    if (plan) {
      form.setFieldsValue({
        weeklyHours: plan.weeklyHours,
        duration: durationOf(plan),
        priceLira: plan.priceKurus / 100,
        monthlyQuestions: plan.monthlyQuestions,
        isFeatured: plan.isFeatured,
        isActive: plan.isActive,
      });
    }
  }, [plan, form]);

  async function save(values: Values) {
    const priceKurus = Math.round(values.priceLira * 100);
    try {
      if (isNew) {
        await api("/api/office/plans", {
          method: "POST",
          body: {
            weeklyHours: values.weeklyHours,
            durationMonths: values.duration.startsWith("m:")
              ? Number(values.duration.slice(2))
              : null,
            seasonId: values.duration.startsWith("s:") ? values.duration.slice(2) : null,
            priceKurus,
            monthlyQuestions: values.monthlyQuestions,
            isFeatured: values.isFeatured,
            isActive: values.isActive,
          },
        });
      } else {
        await api(`/api/office/plans/${encodeURIComponent(id)}`, {
          method: "PATCH",
          body: {
            priceKurus,
            monthlyQuestions: values.monthlyQuestions,
            isFeatured: values.isFeatured,
            isActive: values.isActive,
          },
        });
      }
      await queryClient.invalidateQueries();
      open?.({ type: "success", message: "Plan kaydedildi." });
      navigate("/plans");
    } catch (saveError) {
      open?.({
        type: "error",
        message: saveError instanceof Error ? saveError.message : "Hata oluştu.",
      });
    }
  }

  if (error) return <Alert type="error" showIcon message={error.message} />;

  const fields = (
    <Form<Values>
      form={form}
      layout="vertical"
      style={{ maxWidth: 480 }}
      initialValues={{ duration: "m:1", monthlyQuestions: 0, isFeatured: false, isActive: true }}
      onFinish={save}
    >
      <Form.Item
        label="Haftalık saat"
        name="weeklyHours"
        rules={[{ required: true, message: "Haftalık saati gir." }]}
        extra={isNew ? undefined : "Oluşturulduktan sonra değişmez."}
      >
        <InputNumber min={1} max={40} disabled={!isNew} style={{ width: "100%" }} />
      </Form.Item>
      <Form.Item
        label="Süre"
        name="duration"
        rules={[{ required: true, message: "Süreyi seç." }]}
        extra={
          isNew
            ? "Dönem paketleri sınav gününe kadar sürer; dönemleri Planlar → Dönemler'den eklersin."
            : "Oluşturulduktan sonra değişmez."
        }
      >
        <Select options={durations} disabled={!isNew} showSearch optionFilterProp="label" />
      </Form.Item>
      <Form.Item
        label={isSeason ? "Haftalık fiyat (TL)" : "Toplam fiyat (TL)"}
        name="priceLira"
        rules={[{ required: true, message: "Fiyatı gir." }]}
        extra={
          isSeason
            ? "Satış fiyatı: haftalık fiyat × sınava kalan hafta. Yeni satışlara uygulanır."
            : "Yeni satışlara uygulanır; mevcut abonelikler dönemlerinin sonuna kadar etkilenmez."
        }
      >
        <InputNumber min={1} step={100} precision={2} style={{ width: "100%" }} />
      </Form.Item>
      <Form.Item
        label="Aylık soru hakkı"
        name="monthlyQuestions"
        rules={[{ required: true, message: "Soru hakkını gir." }]}
        extra="Soru Çözüm: öğrencinin aboneliğinin her ayında sorabileceği soru sayısı. 0 ise bu planda soru sorulamaz."
      >
        <InputNumber min={0} max={1000} precision={0} style={{ width: "100%" }} />
      </Form.Item>
      <Form.Item
        label="Popüler"
        name="isFeatured"
        valuePropName="checked"
        extra="Her sürede (ve her dönemde) tek bir plan popüler olur; seçince diğerinin işareti kalkar."
      >
        <Switch />
      </Form.Item>
      <Form.Item
        label="Satışta"
        name="isActive"
        valuePropName="checked"
        extra="Satıştan kaldırılan plan yeni satışta görünmez; mevcut aboneliklere dokunulmaz."
      >
        <Switch />
      </Form.Item>
    </Form>
  );

  return isNew ? (
    <Create title="Yeni plan" saveButtonProps={{ onClick: () => form.submit() }}>
      {fields}
    </Create>
  ) : (
    <Edit
      title={plan ? plan.name : "Plan"}
      isLoading={!plan}
      saveButtonProps={{ onClick: () => form.submit() }}
      canDelete={false}
      headerButtons={() => null}
    >
      <Typography.Paragraph type="secondary">
        Değişiklikler Kayıtlar&apos;a yazılır.
      </Typography.Paragraph>
      {fields}
    </Edit>
  );
}
